import {
  actionCard,
  toneOf,
  type ActionCardKey,
  type DashboardActionCard,
  type DashboardLimits,
  type Tone,
} from "@estelle/domain";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { ageLabel } from "@/modules/dashboard/format";
import { actionCardLink } from "@/modules/dashboard/links";

/** What each card says it opens, as the top corner of the card has it. */
const OPENS: Record<ActionCardKey, string> = {
  cod_awaiting_confirmation: "Orders",
  not_shipped_24h: "Orders",
  attempt_failed: "Shipments",
  cod_payment_pending: "Orders",
  no_update: "Shipments",
  returns_to_receive: "Returns",
  low_stock: "Low Stock",
  my_tasks: "Tasks",
};

/**
 * A card's colour is never the only thing that says so: an amber card says
 * "Needs attention" and a red one "Over limit", and a red card also carries a
 * stripe down its left edge.
 */
const TONE_PILL: Record<Tone, { text: string; className: string }> = {
  ok: {
    text: "All clear",
    className: "bg-tint-green text-tint-green-foreground",
  },
  amber: {
    text: "Needs attention",
    className: "bg-tint-orange text-tint-orange-foreground",
  },
  red: {
    text: "Over limit",
    className: "bg-tint-red text-tint-red-foreground",
  },
};

const TONE_CARD: Record<Tone, string> = {
  ok: "",
  amber: "border-warning",
  red: "border-destructive shadow-[inset_4px_0_0_var(--destructive)]",
};

function secondLine(card: DashboardActionCard, now: number): string {
  if (card.count === 0) return "Nothing waiting";
  if (card.extra) {
    return `${String(card.extra.count)} ${card.extra.label}`;
  }
  const age = ageLabel(card.oldestAt, now);
  return age ? `Oldest ${age}` : " ";
}

function LiveCard({
  card,
  limits,
  now,
}: {
  card: DashboardActionCard;
  limits: DashboardLimits;
  now: number;
}) {
  const definition = actionCard(card.key);
  const link = actionCardLink(card);
  const tone = toneOf(card.count, limits[card.key]);
  const pill = TONE_PILL[tone];

  const body = (
    <>
      <span className="text-muted-foreground absolute top-3 right-3 inline-flex items-center gap-1 text-xs">
        <span className="hidden sm:inline">{OPENS[card.key]}</span>
        <ArrowRight aria-hidden className="size-3" />
      </span>
      <span className="text-muted-foreground min-h-8 pr-5 text-xs sm:pr-16">
        {definition.title}
      </span>
      <span className="text-3xl leading-tight font-semibold tabular-nums">
        {String(card.count ?? 0)}
      </span>
      <span className="text-muted-foreground text-xs">
        {secondLine(card, now)}
      </span>
      <span
        className={cn(
          "mt-1 inline-block w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold",
          pill.className,
        )}
      >
        {pill.text}
      </span>
    </>
  );

  const className = cn(
    "bg-card text-card-foreground focus-visible:ring-ring/50 relative flex min-w-0 flex-col gap-0.5 rounded-md border p-3 transition-colors outline-none hover:border-primary focus-visible:ring-[3px]",
    TONE_CARD[tone],
  );

  if (!link) return <div className={className}>{body}</div>;
  return (
    <Link
      to={link.to}
      search={link.search}
      className={className}
      aria-label={`${definition.title}: ${String(card.count ?? 0)}. ${pill.text}. Opens ${OPENS[card.key]}.`}
    >
      {body}
    </Link>
  );
}

/**
 * The cards with a number to show (#642). A card whose count waits on work not
 * built yet (#465) took the first two slots and showed a dash, so it is left
 * out until it can count; the adapter still returns it as a placeholder.
 */
function liveActionCards(
  cards: readonly DashboardActionCard[],
): DashboardActionCard[] {
  return cards.filter((card) => card.status === "live");
}

export function ActionCards({
  cards,
  limits,
  now,
}: {
  cards: readonly DashboardActionCard[];
  limits: DashboardLimits;
  now: number;
}) {
  const shown = liveActionCards(cards);
  if (shown.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        None of the lists these cards open are open to your role.
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {shown.map((card) => (
        <li key={card.key} className="flex min-w-0 flex-col [&>*]:flex-1">
          <LiveCard card={card} limits={limits} now={now} />
        </li>
      ))}
    </ul>
  );
}
